<template>
  <div class="mt-6 space-y-6">
    <div v-if="pipelineData.length" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="phase in pipelineData"
        :key="phase.progress_label"
        class="bg-white dark:bg-gray-800 overflow-hidden shadow dark:shadow-gray-900/50 rounded-lg"
      >
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <component
                :is="getIconForPhase(phase.progress_label)"
                :class="getColorForPhase(phase.progress_label)"
                class="h-8 w-8"
                aria-hidden="true"
              />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  {{ phase.progress_label || 'Onbekend' }} ({{ phase.count || 0 }})
                </dt>
                <dd class="text-lg font-medium text-gray-900 dark:text-white">
                  € {{ formatCurrency(phase.total_revenue || 0) }}
                </dd>
                <dd class="text-sm text-gray-600 dark:text-gray-400">
                  Reëel: € {{ formatCurrency(phase.weighted_revenue || 0) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 dark:bg-gray-700/50 px-5 py-3">
          <div class="text-sm">
            <router-link
              to="/concretiseren"
              :class="getLinkColorForPhase(phase.progress_label)"
              class="font-medium hover:opacity-75"
            >
              Bekijk {{ phase.progress_label?.toLowerCase() || 'prospects' }}
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="py-12 text-center">
      <div
        class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-purple-600 mx-auto"
      ></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Archief laden...</p>
    </div>

    <div v-else-if="error" class="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
      {{ error }}
    </div>

    <template v-else-if="data">
      <HoursRevenueLineChart :data="data" />
      <HoursRevenueTable :data="data" :derden-omzet-data="derdenOmzetData" />
    </template>

    <FunnelForecast />
    <EmployeePipelineView :reload-trigger="reloadTrigger" />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { FunnelIcon, BriefcaseIcon, EyeIcon } from '@heroicons/vue/24/outline'
import api from '@/services/api.js'
import { hoursService } from '@/services/hoursService.js'
import { servicesService } from '@/services/adminService.js'
import FunnelForecast from './FunnelForecast.vue'
import HoursRevenueLineChart from './HoursRevenueLineChart.vue'
import HoursRevenueTable from './HoursRevenueTable.vue'
import EmployeePipelineView from './EmployeePipelineView.vue'

const props = defineProps({
  reloadTrigger: { type: Number, default: 0 }
})

const data = ref(null)
const derdenOmzetData = ref(null)
const pipelineData = ref([])
const isLoading = ref(false)
const error = ref('')

async function loadData() {
  isLoading.value = true
  error.value = ''
  try {
    const [hoursData, derdenData] = await Promise.all([
      hoursService.getRevenuePerTeam(),
      servicesService.getDerdenOmzet()
    ])
    data.value = hoursData
    derdenOmzetData.value = derdenData
  } catch {
    error.value = 'Kon archief data niet laden'
  } finally {
    isLoading.value = false
  }
}

async function loadPipelineData() {
  try {
    const response = await api.get('/prospects-leads/pipeline')
    if (response.data.success) {
      pipelineData.value = response.data.data.by_phase || []
    }
  } catch (err) {
    console.error('Error loading pipeline data:', err)
    pipelineData.value = []
  }
}

function getIconForPhase(phase) {
  switch (phase) {
    case 'Lead fase':
      return FunnelIcon
    case 'Verkenningsfase':
      return EyeIcon
    case 'Prospect fase':
      return BriefcaseIcon
    default:
      return FunnelIcon
  }
}

function getColorForPhase(phase) {
  switch (phase) {
    case 'Lead fase':
      return 'text-blue-600'
    case 'Verkenningsfase':
      return 'text-yellow-600'
    case 'Prospect fase':
      return 'text-green-600'
    default:
      return 'text-gray-600'
  }
}

function getLinkColorForPhase(phase) {
  switch (phase) {
    case 'Lead fase':
      return 'text-blue-600 hover:text-blue-500'
    case 'Verkenningsfase':
      return 'text-yellow-600 hover:text-yellow-500'
    case 'Prospect fase':
      return 'text-green-600 hover:text-green-500'
    default:
      return 'text-gray-600 hover:text-gray-500'
  }
}

function formatCurrency(amount) {
  if (!amount) return '0'
  return new Intl.NumberFormat('nl-NL').format(Math.round(amount))
}

watch(
  () => props.reloadTrigger,
  () => {
    loadData()
    loadPipelineData()
  }
)

onMounted(() => {
  loadData()
  loadPipelineData()
})
</script>
